import React, { useRef, useMemo } from 'react';
import { sortBy } from 'lodash';
import { useFunction } from '@laigu/store';
import ClientAvatarTag from './client-avatar-tag';
import Anchor from './client-anchor';
import ConvInfo from './conv-lite-info';
import SourceInfo from '@/component/business/visitor-block/client-source-info/index';
import Card from './client-card';
import Reffer from './reffer';
import { CardSubmitParams, IdParams, CardFieldProps, VisitPage } from './typings';

import style from './index.less';

import { Provider } from 'react-redux';
import store from 'chat/store';

type TabDataType = {
  order: number;
  label: string;
  value: string | number;
};

const defaultClientTabData: Array<TabDataType> = [
  { label: '访问', value: 'source', order: 10 },
  { label: '名片', value: 'card', order: 20 }
];

type ExpandTabExampleItem = {
  order: number;
  label: string;
  value: string | number;
  component: () => JSX.Element | string;
};

const defaultExpandTab: Array<ExpandTabExampleItem> = [];

type VisitorBlockProps = {
  visitInfo: VisitInfo;
  convId: number;
  trackId: string;
  tagList: Array<number>;
  updateAttr: (attrs: IdParams & { attrs: CardSubmitParams }) => Promise<void>;
  inBlackList?: boolean;
  onAddInBlacklist?: (params: IdParams) => void;
  onRemoveInBlacklist?: (params: IdParams) => void;
  customerAttrs: Record<string, string>;
  customerField: Array<CardFieldProps>;
  attrConfig: Array<CardFieldProps>;
  isOnline?: boolean;
  visitorPage?: Array<VisitPage>;
  onAddClientTag?: (param: IdParams & { tagId: number }) => void;
  onDeleteClientTag?: (param: IdParams & { tagId: number }) => void;
  disabledKeydown?: boolean;
  clientTabData?: Array<TabDataType>;
  expandTab?: Array<ExpandTabExampleItem>;
  advertise?: Record<string, string>;
  convLiteInfo?: Record<string, string>;
  source?: string;
  subSource?: string;
};

interface VisitorBlockType extends React.FC<VisitorBlockProps> {
  Reffer: React.FC<{ visitInfo: VisitInfo }>;
}

const VisitorBlock: VisitorBlockType = (props: VisitorBlockProps) => {
  const {
    visitInfo = {
      id: ''
    },
    convId,
    trackId,
    tagList,
    updateAttr,
    inBlackList,
    onAddInBlacklist,
    onRemoveInBlacklist,
    customerAttrs,
    customerField,
    attrConfig,
    isOnline,
    visitorPage = [],
    onAddClientTag,
    onDeleteClientTag,
    disabledKeydown,
    clientTabData,
    expandTab,
    advertise,
    convLiteInfo,
    source,
    subSource
  } = props;
  const customerFieldParam = customerField || [];
  const expandTabParam = expandTab || defaultExpandTab;
  const expandComponentMap: Record<string, () => string | JSX.Element> = useMemo(() => {
    if (expandTabParam && expandTabParam.length > 0) {
      return expandTabParam.reduce((prev, now) => {
        const { value, component } = now;
        return {
          ...prev,
          [value]: () => component
        };
      }, {});
    }
    return {};
  }, [expandTabParam]);
  const clientTabDataParam = useMemo(() => {
    const data = clientTabData || defaultClientTabData;
    return sortBy([...data, ...expandTabParam], 'order');
  }, [clientTabData, defaultClientTabData, expandTabParam]);

  const scrollContainer = useRef<HTMLDivElement>(null);

  const idParams = useMemo(() => {
    let result: IdParams = {
      trackId,
      visitId: visitInfo.id || ''
    };
    if (convId) {
      result = {
        ...result,
        convId
      };
    }
    return result;
  }, [trackId, visitInfo, convId]);

  const clientCardFunc = useFunction('client_card');
  const clientBasicFunc = useFunction('client_basic_v2');
  let permCustomerField = [...customerFieldParam];

  if (clientBasicFunc === false) {
    permCustomerField = permCustomerField.filter(t => t.category === 'main');
  }

  const getInfoBox = () => {
    const tabComponentMap: Record<string, () => string | JSX.Element> = {
      conv: () => <ConvInfo convLiteInfo={convLiteInfo} attrs={attrConfig} />,
      source: () => (
        <SourceInfo
          visitInfo={visitInfo}
          isOnline={isOnline}
          visitorPage={visitorPage}
          customerAttrs={customerAttrs}
          advertise={advertise}
          source={source}
          subSource={subSource}
        />
      ),
      card: () =>
        clientCardFunc ? (
          <Provider store={store}>
            <Card
              trackId={trackId}
              convId={convId}
              attrs={customerAttrs}
              cardField={permCustomerField}
              disabledKeydown={disabledKeydown}
              updateAttr={attrs =>
                updateAttr({
                  attrs,
                  ...idParams
                })
              }
            />
          </Provider>
        ) : (
          <></>
        )
    };
    return (
      <>
        {clientTabDataParam.map(val => {
          const { value } = val;
          const genItem = tabComponentMap[value] || expandComponentMap[value];
          return (
            <div key={value} id={`${value}`}>
              {genItem()}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <>
      <ClientAvatarTag
        visitInfo={visitInfo}
        tagList={tagList}
        inBlackList={inBlackList}
        onAddInBlacklist={onAddInBlacklist}
        onRemoveInBlacklist={onRemoveInBlacklist}
        idParams={idParams}
        name={customerAttrs.name}
        onAddClientTag={onAddClientTag}
        onDeleteClientTag={onDeleteClientTag}
        disabledKeydown={disabledKeydown}
      />
      <Anchor scrollContainer={scrollContainer.current} nav={clientTabDataParam} />
      <div className={style['info-box']} ref={scrollContainer}>
        {getInfoBox()}
      </div>
    </>
  );
};

VisitorBlock.Reffer = Reffer;

export default VisitorBlock;
